import { Component, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { brandifyNode } from '@/components/brand/kindlumen'
import { Button } from '@/components/ui/button'
import { logClientError } from '@/lib/logger'

type Props = {
  children: ReactNode
}

type State = {
  hasError: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    logClientError('App error boundary caught', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false })
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div className="section-shell flex min-h-screen items-center justify-center py-12">
        <div className="panel max-w-2xl text-center p-10">
          <div className="eyebrow mx-auto">500</div>
          <h1 className="mt-6 text-4xl font-semibold leading-[1.16] sm:text-5xl sm:leading-[1.12]">
            Something went off-script.
          </h1>
          <p className="mt-4 text-lg leading-8 text-ink-soft">
            {brandifyNode(
              'KindLumen ran into an unexpected error rendering this view. Reload to try again, or jump back to a known-good area of the product.',
            )}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button onClick={() => window.location.reload()}>Reload page</Button>
            <Button asChild variant="secondary" onClick={this.handleReset}>
              <Link to="/">Go to homepage</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
